import { MultiOption, MultiOptions, ProductVariation, SingleOptions } from 'editor/src/store/variants/types';

import isObjectMatchingDepOptions from './isObjectMatchingDepOptions';

function isMatchingOption(controlKey: string, variation: ProductVariation, option: MultiOption, ignoreDeps: boolean) {
  return option.value === variation[controlKey] && (ignoreDeps || isObjectMatchingDepOptions(variation, option));
}

function getAllMatchingVariations(
  variations: ProductVariation[],
  multiOptions: MultiOptions,
  singleOptions: SingleOptions,
  singleMode: boolean,
  replaceControlKeys: string[],
): ProductVariation[] {
  const multiOptionKeys = Object.keys(multiOptions);
  const singleOptionsKeys = Object.keys(singleOptions).filter((key) => singleMode || replaceControlKeys.includes(key));

  // Early return if no filters
  if (singleOptionsKeys.length === 0 && multiOptionKeys.length === 0) {
    return variations;
  }

  // Check if any multi options have dependencies - if so, use original logic to preserve correctness
  const hasDependencies = multiOptionKeys.some(key =>
    multiOptions[key].some(option => option.dependedOptions && option.dependedOptions.length > 0)
  );

  if (hasDependencies) {
    // Use original logic for dependency handling to maintain backward compatibility
    return variations.filter(
      (variation) =>
        singleOptionsKeys.every((key) => variation[key] === singleOptions[key]) &&
        multiOptionKeys.every((key) =>
          multiOptions[key].some((option) => isMatchingOption(key, variation, option, singleMode)),
        ),
    );
  }

  // Optimized path for simple cases without dependencies
  const multiOptionSets = new Map<string, Set<string>>();
  for (const key of multiOptionKeys) {
    const values = new Set(multiOptions[key].map(option => option.value));
    multiOptionSets.set(key, values);
  }

  return variations.filter((variation) => {
    // Check single options first (typically fewer and faster)
    for (const key of singleOptionsKeys) {
      if (variation[key] !== singleOptions[key]) {
        return false;
      }
    }

    // Check multi options using pre-built sets for O(1) lookups
    for (const key of multiOptionKeys) {
      const valueSet = multiOptionSets.get(key);
      if (!valueSet?.has(variation[key])) {
        return false;
      }
    }

    return true;
  });
}

export default getAllMatchingVariations;
